import React from "react";
import { useApp } from "@/context/AppContext";
import { DISPLAY_CURRENCIES, ccyMeta } from "@/lib/fx";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Check, ChevronDown, Home, RotateCcw } from "lucide-react";
import { CurrencyFlag } from "@/components/CurrencyFlag";

const Row = ({ c, active, home, onPick }) => (
  <DropdownMenuItem onClick={() => onPick(c.code)} className="flex items-center justify-between"
    data-testid={`topbar-currency-${c.code}`}>
    <span className="flex items-center gap-2 min-w-0">
      <CurrencyFlag code={c.code} size={18} />
      <span className="text-sm">{c.code}</span>
      <span className="text-xs text-muted-foreground truncate">{c.label}</span>
      {home && <Home className="h-3 w-3 text-[color:var(--tharwy-lime-700)] shrink-0" />}
    </span>
    {active && <Check className="h-4 w-4 text-primary shrink-0" />}
  </DropdownMenuItem>
);

/** Top-bar display-currency picker — defaults to the client's home currency. */
export const CurrencySelect = () => {
  const { user, displayCcy, setDisplayCcy, homeCcy, ccyPinned, resetDisplayCcy } = useApp();
  const m = ccyMeta(displayCcy);
  const home = ccyMeta(homeCcy);
  const rest = DISPLAY_CURRENCIES.filter((c) => c.code !== homeCcy);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="nav-chip gap-1.5 px-2.5"
          data-testid="topbar-currency-toggle">
          <CurrencyFlag code={m.code} size={18} />
          <span className="text-xs font-semibold">{m.code}</span>
          <ChevronDown className="nav-caret h-3.5 w-3.5 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="topbar-currency-menu">
        <DropdownMenuLabel className="flex items-center gap-1.5">
          <Home className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)]" />
          Home currency
        </DropdownMenuLabel>
        <Row c={home} active={displayCcy === home.code} home onPick={setDisplayCcy} />
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Show totals in</DropdownMenuLabel>
        <div className="max-h-64 overflow-auto">
          {rest.map((c) => (
            <Row key={c.code} c={c} active={displayCcy === c.code} onPick={setDisplayCcy} />
          ))}
        </div>
        {ccyPinned && displayCcy !== homeCcy && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => resetDisplayCcy(user)} data-testid="topbar-currency-reset">
              <RotateCcw className="h-4 w-4 me-2" /> Back to {homeCcy} (my home currency)
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-[11px] leading-relaxed text-muted-foreground">
          Totals are converted at a fixed indicative rate. Trades, fees and balances stay
          booked in their real currency.
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Honest reminder that non-USD figures are indicative conversions. */
export const RateBanner = () => {
  const { displayCcy, homeCcy } = useApp();
  const m = ccyMeta(displayCcy);
  if (m.code === "USD") return null;
  return (
    <div className="border-t border-border/60 bg-secondary/50 px-4 py-1.5 text-center text-[11px] text-muted-foreground"
      data-testid="display-currency-banner">
      Showing every total in {m.code}{displayCcy === homeCcy ? " — your home currency" : ""} at an
      indicative 1 USD = {m.rate} {m.code} · trades and balances remain booked in their real currency
    </div>
  );
};
